import { standardDrawing } from '@moleculeone/draw';
import { DrawnMol, Mol, Reaction } from '@moleculeone/types';

import { MolDrawers, ReactionHighlights, getPlainMolDrawers } from './getPlainMolDrawers';
import { ReversedSubstratesMap } from './reverseMapping';

type ShouldColorSubstrate = (mapId: string, substrateIndex: number) => boolean;

type CompoundColorMap = {
  [mapId: string]: string;
};

export const SubstrateColors = ['#007bff', '#28a745', '#dc3545', '#ffc107'];

export const HoverType = {
  HOVER_NONE: 'HOVER_NONE',
  HOVER_ALL: 'HOVER_ALL',
  HOVER_ATOM: 'HOVER_ATOM',
  HOVER_MOL: 'HOVER_MOL',
};

function getColorMapMolDrawer(
  productMap: Reaction['productMap'] = [],
  substratesMap: Reaction['substratesMap'] = [],
  classToColor: CompoundColorMap
): MolDrawers {
  const drawProduct = (mol: Mol): DrawnMol => {
    const drawnMol = standardDrawing(mol) as DrawnMol;
    for (const [key, atom] of Object.entries(drawnMol.atoms)) {
      const mapId = productMap[Number(key)];
      atom.color = mapId == null ? 'black' : classToColor[mapId];
    }
    return drawnMol;
  };

  const drawSubstrate = (mol: Mol, substrateIdx: number): DrawnMol => {
    const drawnMol = standardDrawing(mol) as DrawnMol;
    for (const [key, atom] of Object.entries(drawnMol.atoms)) {
      const mapId = substratesMap[substrateIdx][Number(key)];
      atom.color = mapId == null ? 'black' : classToColor[mapId];
    }
    return drawnMol;
  };

  return {
    drawProduct,
    drawSubstrate,
  };
}

function filteredColorMap(mapToSubstrates: ReversedSubstratesMap, shouldColor: ShouldColorSubstrate): CompoundColorMap {
  const classToColor: CompoundColorMap = {};

  for (const [mapId, [substrateIdx]] of Object.entries(mapToSubstrates)) {
    const should = shouldColor(mapId, substrateIdx);
    classToColor[mapId] = should ? SubstrateColors[substrateIdx % 4] : 'black';
  }

  return classToColor;
}

export function getMolDrawers(
  productMap: Reaction['productMap'],
  substratesMap: Reaction['substratesMap'],
  highlights: ReactionHighlights,
  mapToSubstrates: ReversedSubstratesMap,
  hoverType: string,
  hoveredSubstrateIndex?: number
): MolDrawers {
  switch (hoverType) {
    case HoverType.HOVER_NONE:
      return getPlainMolDrawers(mapToSubstrates, highlights);
    case HoverType.HOVER_ALL:
    case HoverType.HOVER_ATOM: {
      const shouldColor = (): boolean => true;
      const classToColor = filteredColorMap(mapToSubstrates, shouldColor);
      return getColorMapMolDrawer(productMap, substratesMap, classToColor);
    }
    case HoverType.HOVER_MOL: {
      const hoveredIndex = hoveredSubstrateIndex;
      const shouldColor = (mapId: string, substrateIdx: number): boolean => substrateIdx === hoveredIndex;
      const classToColor = filteredColorMap(mapToSubstrates, shouldColor);
      return getColorMapMolDrawer(productMap, substratesMap, classToColor);
    }
    default:
      throw new Error();
  }
}
